import {
  Http,
  Request,
  RequestOptions,
  RequestOptionsArgs,
  Response,
  XHRBackend,
  Headers
} from '@angular/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { EventsManagerService } from '../common/events-manager.service';
import { AppConfigurationService } from '../common/app-configuration.service';
import { ConnectionMonitorService } from '../common/connection-monitor.service';
import { AuthService } from '../auth/auth.service';
import { StorageService } from '../storage/storage.service';

import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/timeout';
// @dynamic
@Injectable()
export class HttpInterceptor extends Http {
  private static _stopRequest: boolean = false;
  private static _enqueueRequests: boolean = false;

  public static get enqueueRequests(): boolean {
    return HttpInterceptor._enqueueRequests;
  }

  public static get stopRequest(): boolean {
    return HttpInterceptor._stopRequest;
  }

  private notifier: Subject<any> = new Subject<any>();

  constructor(
    backend: XHRBackend,
    options: RequestOptions,
    public http: Http,
    public eventsManagerService: EventsManagerService,
    public AppConfiguration: AppConfigurationService,
    private connectionMonitorService: ConnectionMonitorService,
    private authService: AuthService,
    private storage: StorageService
  ) {
    super(backend, options);

    this.eventsManagerService.onTokenRefreshStart.subscribe(() => {
      HttpInterceptor._enqueueRequests = true;
    });

    this.eventsManagerService.onServerConnectivityError.subscribe(() => {
      if (this.AppConfiguration.currentConfig.useLocalConnection === true ||
        this.AppConfiguration.currentConfig.useOfflineOperations ===  true) {
          HttpInterceptor._enqueueRequests = true;
      }
    });

    this.eventsManagerService.onNoConnection.subscribe(() => {
      HttpInterceptor._enqueueRequests = false;
      HttpInterceptor._stopRequest = true;
      this.drainQueue();
    });

    this.eventsManagerService.onRecoverConnection.subscribe(() => {
      HttpInterceptor._enqueueRequests = false;
      HttpInterceptor._stopRequest = false;
      this.drainQueue();
    });

    this.eventsManagerService.onConnectivityRecovered.subscribe(() => {
      HttpInterceptor._enqueueRequests = false;
      HttpInterceptor._stopRequest = false;
      this.drainQueue();
    });

    this.eventsManagerService.onTokenRefreshed.subscribe(
      ({ status, newAccessToken }) => {
        HttpInterceptor._enqueueRequests = false;

        if (status) {
          this.drainQueue(newAccessToken);
        }
      }
    );

    this.eventsManagerService.onTokenRefreshError.subscribe(() => {
      HttpInterceptor._enqueueRequests = false;
    });

    this.eventsManagerService.onRestoreNetworkStatus.subscribe(() => {
      HttpInterceptor._enqueueRequests = false;
    });
  }

  private drainQueue(newAccessToken?: string) {
    this.notifier.next({
      newAPIUrl: this.AppConfiguration.currentAPIURL,
      newAccessToken
    });
  }

  private changeAccessTokenForRequest(
    headers: Headers,
    newAccessToken?: string
  ): Headers {
    // Prioritize apiKey over accessToken
    if (this.AppConfiguration.currentConfig.apiKey) {
      headers.set('Authorization', `SMARTENIT-API-KEY ${this.AppConfiguration.currentConfig.apiKey}`);
    } else if (newAccessToken) {
      headers.set('authorization', newAccessToken);
    }

    return headers;
  }

  private changeAPIUrlForRequest(url: string, newAPIUrl: string): string {
    return url.replace(/.*smartenit\.io\/v2/, newAPIUrl);
  }

  private getNextRequest(
    newAccessToken: string,
    newAPIUrl: string,
    prevRequest: Request,
    options?: RequestOptionsArgs
  ) {
    const adjustedUrl = new Request({
      body: prevRequest.getBody(),
      headers: this.changeAccessTokenForRequest(
        prevRequest.headers,
        newAccessToken
      ),
      method: prevRequest.method,
      responseType: prevRequest.responseType,
      url: this.changeAPIUrlForRequest(prevRequest.url, newAPIUrl)
    });
    return super.request(adjustedUrl, options);
  }

  public request(
    url: string | Request,
    options?: RequestOptionsArgs
  ): Observable<Response> {
    let localUrl: string;

    if (typeof url == 'string') {
      localUrl = url;
    } else {
      localUrl = (url as Request).url;
    }

    let urlToRequest: any;

    if (url instanceof Request) {
      urlToRequest = url;
    } else if (typeof url == 'string') {
      urlToRequest = new Request({ url });
    }
    if (HttpInterceptor._stopRequest &&
      localUrl.indexOf('ping') < 0 &&
      localUrl.indexOf('i18n') < 0 &&
      localUrl.indexOf('oauth2/token') < 0
      ) {
      return Observable.empty();
    }
    if (
      HttpInterceptor._enqueueRequests &&
      localUrl.indexOf('oauth2/token') < 0 &&
      localUrl.indexOf('websockets/auth') < 0 &&
      localUrl.indexOf('users') < 0 &&
      localUrl.indexOf('ping') < 0
    ) {
      return this.notifier
        .asObservable()
        .flatMap(({ newAccessToken, newAPIUrl }) => {
          const adjustedUrl = new Request({
            body: urlToRequest.body,
            headers: this.changeAccessTokenForRequest(
              urlToRequest.headers,
              newAccessToken
            ),
            method: urlToRequest.method,
            responseType: urlToRequest.responseType,
            search: urlToRequest.search,
            url: this.changeAPIUrlForRequest(urlToRequest.url, newAPIUrl)
          });

          return super.request(adjustedUrl, options);
        });
    }

    const self = this;

    if (localUrl.indexOf('build/') < 0) {
      return super
        .request(url, options)
        .timeout(30 * 1000)
        .map((response) => {
          if (localUrl.indexOf('oauth2/token') >= 0) {
            const jsonResponse = response.json();

            if (jsonResponse && jsonResponse.access_token) {
              HttpInterceptor._enqueueRequests = false;
              this.notifier.next({
                newAPIUrl: this.AppConfiguration.currentAPIURL,
                newAccessToken: 'Bearer ' + jsonResponse.access_token
              });
            }
          }
          if (<number> <any> this.storage.get('connectionTries') != 0) {
            this.eventsManagerService.trigger(EventsManagerService.ON_RECOVER_CONNECTION);
          }
          return response;
        })
        .catch((error: Response | any) => {
          console.log('intercepted error: ', error, url);
          if (error.status === 401) {
            this.authService.authenticated = false;
            self.eventsManagerService.trigger(
              EventsManagerService.ON_UNAUTHENTICATED, error);
          }

          if (error.status === 403) {
            self.eventsManagerService.trigger(
              EventsManagerService.ON_FORBIDDEN_REQUEST, error);
          }

          if (
            error.status == 0 ||
            error.status == 503
          ) {
            if (
              localUrl.indexOf('ping') < 0 &&
              localUrl.indexOf('i18n') < 0 &&
              this.connectionMonitorService.isMonitoring
            ) {

              self.eventsManagerService.trigger(
                EventsManagerService.ON_SERVER_CONNECTIVITY_ERROR,
                { error, enqueueRequests: HttpInterceptor.enqueueRequests }
              );
            }

            if (localUrl.indexOf('oauth2/token') >= 0) {
              this.AppConfiguration.restoreInitialConfiguration();
              return Observable.throw(error);
            } else if (
              localUrl.indexOf('devices/reference') >= 0 ||
              localUrl.indexOf('i18n') >= 0
            ) {
              return Observable.throw(error);
            } else {
              return this.notifier
                .asObservable()
                .flatMap(({ newAccessToken, newAPIUrl }) => {
                  const adjustedUrl = new Request({
                    body: urlToRequest.body,
                    headers: this.changeAccessTokenForRequest(
                      urlToRequest.headers,
                      newAccessToken
                    ),
                    method: urlToRequest.method,
                    responseType: urlToRequest.responseType,
                    search: urlToRequest.search,
                    url: this.changeAPIUrlForRequest(
                      urlToRequest.url,
                      newAPIUrl
                    )
                  });

                  return super.request(adjustedUrl, options);
                });
            }
          } else {
            if (
              localUrl.indexOf('oauth2/token') >= 0 &&
              this.connectionMonitorService.isMonitoring
            ) {
              const errorData = error._body
                ? JSON.parse(error._body)
                : undefined;
              let errors =
                (errorData && errorData.error && errorData.error.errors) || [];
              if (errors.length == 0 && errorData && errorData.code) {
                errors = [errorData];
              }
              // 104: Invalid user credentials | 117: Number of login attempts exceeded
              // 400: Token Expired | 500: Server Error (Must be handled by the user )
              const notConnectionErrors: number[] = [104, 117, 114, 115, 400, 500];
              if (
                !errors.length ||
                (notConnectionErrors.find((e) => errors[0].code === e) === undefined)
              ) {
                // HttpInterceptor._enqueueRequests = true;
                self.eventsManagerService.trigger(
                  EventsManagerService.ON_SERVER_CONNECTIVITY_ERROR,
                  { error, enqueueRequests: HttpInterceptor.enqueueRequests }
                );
                this.AppConfiguration.restoreInitialConfiguration();
              }
            } else {
              self.eventsManagerService.trigger(
                EventsManagerService.ON_HTTP_REQUEST_ERROR,
                { request: url, error }
              );
            }
          }

          return Observable.throw(error);
        });
    } else {
      return Observable.empty();
    }
  }
}
